import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="// LEGAL"
      title="Privacy Policy"
      lastUpdated="February 22, 2026"
    >
      <p>
        This Privacy Policy describes how <strong>BroadcastFlow AI</strong>
        (&ldquo;BroadcastFlow&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) collects,
        uses, shares, and protects personal information when you use our website,
        applications, and related services (the &ldquo;Service&rdquo;).
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 Information you provide</h3>
      <ul>
        <li>
          <strong>Account data:</strong> name, email address, station name, role,
          time-zone, and password (hashed). If you sign in via Google, we receive
          your name, email, and profile picture from Google.
        </li>
        <li>
          <strong>Billing data:</strong> handled by Stripe. We store only a
          customer reference, plan, subscription status, and last-4 of payment
          method &mdash; we never see your full card number.
        </li>
        <li>
          <strong>BYOK credentials:</strong> the API keys you connect for
          third-party providers (OpenAI, Google AI, Anthropic, ElevenLabs,
          Stability, Epidemic Sound, etc.). These are encrypted at rest in our
          database and decrypted only at request time to call the provider.
        </li>
        <li>
          <strong>Content you upload:</strong> audio files, scripts, brand kits,
          logos, generated images and video, blog posts, social posts, RSS feeds,
          podcast episodes, schedules, and similar broadcast content.
        </li>
        <li>
          <strong>Communications:</strong> support requests, feedback, and any
          information you share when you contact us.
        </li>
      </ul>

      <h3>1.2 Information we collect automatically</h3>
      <ul>
        <li>
          <strong>Usage data:</strong> pages viewed, features used, button clicks,
          session length, and error logs &mdash; used to operate, secure, and
          improve the Service.
        </li>
        <li>
          <strong>Device data:</strong> IP address, browser type, operating
          system, time-zone, and approximate location (city-level) derived from
          your IP.
        </li>
        <li>
          <strong>Cookies:</strong> session and preference cookies necessary to
          keep you signed in and remember your settings. We do not use third-party
          advertising cookies.
        </li>
      </ul>

      <h3>1.3 Information from third parties</h3>
      <p>
        When you connect a social platform (e.g. Meta, X, TikTok, YouTube,
        LinkedIn, WhatsApp Business, Spotify, Apple Podcasts), we receive only the
        scopes you authorise &mdash; typically the ability to post on your behalf,
        read your channel metadata, and retrieve basic profile info. We do not read
        private messages or personal data from your followers.
      </p>

      <h2>2. How We Use Information</h2>
      <p>We process personal information to:</p>
      <ul>
        <li>Operate, secure, and improve the Service;</li>
        <li>Generate AI output you request (text, voice, image, video, captions, translations);</li>
        <li>Publish, schedule, or syndicate content to the platforms you connect;</li>
        <li>Process payments, manage subscriptions, and prevent fraud;</li>
        <li>Send transactional and service emails (e.g. provider-down alerts, password resets);</li>
        <li>Provide support and respond to your enquiries;</li>
        <li>Comply with legal obligations and enforce our Terms.</li>
      </ul>

      <p>
        <strong>We do not sell your personal information.</strong> We do not use
        Your Content or AI Output to train our own AI models.
      </p>

      <h2>3. AI Providers and Your Prompts</h2>
      <p>
        When you use AI features, your prompt and any associated content are sent
        to the provider you selected (OpenAI, Anthropic, Google, ElevenLabs, etc.)
        in order to generate a response. Those providers process the data under
        their own terms and privacy policies. Some providers offer &ldquo;no
        training&rdquo; modes when used via API &mdash; we use those defaults
        wherever supported. We recommend reviewing the privacy policies of any
        provider you enable.
      </p>

      <h2>4. Sharing of Information</h2>
      <p>We share personal information only with:</p>
      <ul>
        <li>
          <strong>Service providers</strong> who operate parts of our
          infrastructure (cloud hosting, MongoDB Atlas, email delivery via Resend,
          payment processing via Stripe, error monitoring). These vendors are
          bound by confidentiality and data-protection agreements.
        </li>
        <li>
          <strong>AI &amp; integration providers</strong> you have explicitly
          connected, only to fulfil the requests you initiate.
        </li>
        <li>
          <strong>Authorities</strong> when required by law, valid legal process,
          or to protect rights, safety, or property.
        </li>
        <li>
          <strong>Successors</strong> as part of a merger, acquisition, or sale of
          assets, subject to standard confidentiality safeguards.
        </li>
      </ul>

      <h2>5. International Transfers</h2>
      <p>
        BroadcastFlow operates globally. Your information may be processed and
        stored in countries other than the one in which you live. We use
        appropriate safeguards (such as standard contractual clauses) for transfers
        of personal data from the European Economic Area, the United Kingdom, and
        Switzerland.
      </p>

      <h2>6. Retention</h2>
      <p>
        We retain personal information while your account is active and for a
        reasonable period afterwards to comply with legal obligations, resolve
        disputes, and enforce our agreements. You may delete content at any time
        from the dashboard; some data (e.g. invoices) may be retained longer where
        legally required.
      </p>

      <h2>7. Security</h2>
      <p>
        We protect your data with TLS in transit, encryption at rest for
        sensitive fields (including BYOK keys), role-based access control,
        least-privilege production access, application-level rate limiting, and
        audit logging. No system is perfectly secure; you are responsible for
        keeping your credentials safe and notifying us of any suspected breach
        of your account.
      </p>

      <h2>8. Your Rights</h2>
      <p>
        Depending on where you live, you may have the right to access, correct,
        port, restrict the processing of, or delete your personal information; to
        object to certain processing; and to withdraw consent at any time. To
        exercise these rights, write to <a href="mailto:privacy@broadcastflow.ai">privacy@broadcastflow.ai</a>.
        We will respond within the time required by applicable law.
      </p>
      <p>
        If you are in the European Economic Area, United Kingdom, or Switzerland,
        you also have the right to lodge a complaint with your local data
        protection authority.
      </p>

      <h2>9. Children</h2>
      <p>
        The Service is not directed to children under 16. We do not knowingly
        collect personal information from children. If you believe a child has
        provided us with personal information, please contact us so we can
        delete it.
      </p>

      <h2>10. Cookies &amp; Tracking</h2>
      <p>
        We use a small number of strictly-necessary cookies (e.g. your session
        cookie) and lightweight first-party analytics to understand product
        usage. We do not use third-party advertising or cross-site tracking
        cookies. You can control cookies through your browser settings, but
        disabling them may prevent the Service from working.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will revise the
        &ldquo;Last updated&rdquo; date and, for material changes, notify you by
        email or via the Service before the change takes effect.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions, requests, or concerns about this policy? Write to
        {" "}<a href="mailto:privacy@broadcastflow.ai">privacy@broadcastflow.ai</a>.
      </p>

      <p className="text-xs text-ink-muted mt-12 pt-6 border-t border-line">
        This Privacy Policy is provided as a starting point and has not been
        reviewed by legal counsel. You should consult a qualified lawyer in your
        jurisdiction (and, if you handle EU personal data, a DPO or GDPR
        specialist) before relying on it in production.
      </p>
    </LegalLayout>
  );
}
